import { useCallback, useContext, useMemo } from 'react';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { omit } from 'lodash';
import { sendUsageAction } from 'utilities/usageStats';

const { updateFilter, updateFilterCallback, updateQueryString } = WorkspaceActionCreator;

export enum FACET_STATE {
	ON = 'on',
	OFF = 'off',
	UNSELECTED = 'unselected',
}

function getNullSafeFacets(facetsRaw) {
	return (
		facetsRaw ?? {
			activeFacets: {},
			activeHierarchicalFacets: {},
			textFilter: '',
		}
	);
}

/**
 * useFacetsState handles saving and fetching facet-related information from the WorkspaceContext
 * Adding and Removing facets is as easy as passing in the a property name and a facet name into toggleFacet()
 * Also keeps track of editable facet related parameters, such as textFilter
 */
export const useFacetsState = () => {
	const {
		state: {
			explorer: {
				filters: { facets: facetsRaw },
			},
		},
		dispatch,
	} = useContext(WorkspaceContext);
	const facets = useMemo(() => {
		return getNullSafeFacets(facetsRaw);
	}, [facetsRaw]);
	const { activeFacets, activeHierarchicalFacets, textFilter } = facets;

	// returns an object representation of all of the active facets
	const getActiveFacets = () => {
		return activeFacets;
	};

	// returns an object representation of all of the active hierarchicalFacets
	const getActiveHierarchicalFacets = () => {
		return activeHierarchicalFacets;
	};

	// returns the current searchfilter
	const getTextFilter = () => {
		return textFilter || '';
	};

	/**
	 * updates the current search filter
	 * @param newFilter
	 */
	const updateTextFilter = useCallback(
		(newFilter: string) => {
			dispatch(updateFilter('facets', { ...facets, textFilter: newFilter }));
		},
		[dispatch, facets]
	);

	/**
	 * Determines automatically whether to add or remove a facet.
	 * @param property
	 * @param facet
	 */
	const toggleFacet = (property: string, facet: string) => {
		const state = getFacetState(property, facet);
		if (state === FACET_STATE.ON || state === FACET_STATE.OFF) removeFacet(property, facet);
		else addFacet(property, facet, FACET_STATE.ON);
	};

	/**
	 * Turn on multiple facets from one property.
	 * @param property
	 * @param facets
	 */
	const loadFacets = (property: string, facets: string[]) => {
		facets.forEach((facet) => {
			addFacet(property, facet, FACET_STATE.ON)
		})
	};

	/**
	 * Determines automatically whether to add or remove a facet.
	 * @param property
	 * @param facet
	 * @param isAncestorSelected
	 */
	const toggleHierarchicalFacet = (property: string, facet: string, isAncestorSelected: boolean) => {
		const state = getHierarchicalFacetState(property, facet);
		if (isAncestorSelected) {
			if (state === FACET_STATE.OFF) removeHierarchicalFacet(property, facet);
			else addHierarchicalFacet(property, facet, FACET_STATE.OFF);
		} else {
			if (state === FACET_STATE.ON || state === FACET_STATE.OFF) removeHierarchicalFacet(property, facet);
			else addHierarchicalFacet(property, facet, FACET_STATE.ON);
		}
	};

	/**
	 * exclude a singular facet
	 * @param property
	 * @param facetToExclude
	 */
	const toggleExcludeFacet = (property: string, facetToExclude: string) => {
		const state = getFacetState(property, facetToExclude);
		if (state === FACET_STATE.OFF) removeFacet(property, facetToExclude);
		else addFacet(property, facetToExclude, FACET_STATE.OFF);
	};

	/**
	 * exclude a singular Hierarchicalfacet
	 * excluding will remove results that contain this facet
	 * @param property
	 * @param facetToExclude
	 */
	const toggleExcludeHierarchicalFacet = (property: string, facetToExclude: string) => {
		const state = getHierarchicalFacetState(property, facetToExclude);
		if (state === FACET_STATE.OFF) removeHierarchicalFacet(property, facetToExclude);
		else addHierarchicalFacet(property, facetToExclude, FACET_STATE.OFF);
	};

	/**
	 * pass in a facet and the property it belongs to and it gets added
	 * @param property
	 * @param facetToAdd
	 * @param newState
	 */
	const addFacet = (property: string, facetToAdd: string, newState: FACET_STATE) => {


		dispatch(updateFilterCallback('facets', (facetsRaw) => {
			const facets = getNullSafeFacets(facetsRaw);
			const { activeFacets } = facets;

			let newActiveFacets = {};
			// if the property doesn't exist yet in the active facets, add it
			// otherwise, append the facet being added to that property
			if (activeFacets === null) {
				newActiveFacets = {
					[property]: {
						[facetToAdd]: newState,
					},
				};
			} else {
				const propertyState = activeFacets.hasOwnProperty(property) ? activeFacets[property] : {};
				newActiveFacets = {
					...activeFacets,
					[property]: {
						...propertyState,
						[facetToAdd]: newState,
					},
				};
			}
			return { ...facets, activeFacets: newActiveFacets };
		}));
		if (newState === FACET_STATE.ON) {
			sendUsageAction('FacetAdded', `${property}:${facetToAdd}`);
		}
	};

	/**
	 * pass in a facet and the property it belongs to and it gets added
	 * @param property
	 * @param facetToAdd
	 * @param newState
	 */
	const addHierarchicalFacet = (property: string, facetToAdd: string, newState: FACET_STATE) => {
		let newActiveHierarchicalFacets = {};
		// if the property doesn't exist yet in the active hierarchicalFacets, add it
		// otherwise, append the facet being added to that property
		if (activeHierarchicalFacets === null) {
			newActiveHierarchicalFacets = {
				[property]: {
					[facetToAdd]: newState,
				},
			};
		} else {
			const propertyState = activeHierarchicalFacets.hasOwnProperty(property) ? activeHierarchicalFacets[property] : {};
			newActiveHierarchicalFacets = {
				...activeHierarchicalFacets,
				[property]: {
					...propertyState,
					[facetToAdd]: newState,
				},
			};
		}

		// remove child facets if parent is selected
		if (activeHierarchicalFacets[property]) {
			Object.keys(activeHierarchicalFacets?.[property]).forEach((facet) => {
				if (facet.includes(facetToAdd) && facet !== facetToAdd) {
					delete newActiveHierarchicalFacets[property][facet];
				}
			});
		}

		dispatch(updateFilter('facets', { ...facets, activeHierarchicalFacets: newActiveHierarchicalFacets }));
		if (newState === FACET_STATE.ON) {
			sendUsageAction('HierarchicalFacetAdded', `${property}:${facetToAdd}`);
		}
	};

	/**
	 * pass in a facet and the property it belongs to and it gets removed
	 * @param property
	 * @param facetToRemove
	 */
	const removeFacet = (property: string, facetToRemove: string) => {
		const facetsItems = activeFacets[property];
		const newFacets = omit(facetsItems, facetToRemove);

		let newActiveFacets = {
			...activeFacets,
			[property]: newFacets,
		};

		// remove property if there are no more items inside
		if (Object.keys(newActiveFacets[property]).length < 1) {
			delete newActiveFacets[property];
		}

		// reset facets if there are no more properties left inside
		if (Object.keys(newActiveFacets).length < 1) {
			dispatch(updateFilter('facets', { ...facets, activeFacets: {} }));
			dispatch(updateQueryString('facets', null));
		} else {
			dispatch(updateFilter('facets', { ...facets, activeFacets: newActiveFacets }));
		}
	};

	/**
	 * pass in a facet and the property it belongs to and it gets removed
	 * @param property
	 * @param facetToRemove
	 */
	const removeHierarchicalFacet = (property: string, facetToRemove: string) => {
		const facetsItems = activeHierarchicalFacets[property];
		const newFacets = omit(facetsItems, facetToRemove);

		let newActiveHierarchicalFacets = {
			...activeHierarchicalFacets,
			[property]: newFacets,
		};

		// remove property if there are no more items inside
		if (Object.keys(newActiveHierarchicalFacets[property]).length < 1) {
			delete newActiveHierarchicalFacets[property];
		}

		// reset hierarchicalFacets if there are no more properties left inside
		if (Object.keys(newActiveHierarchicalFacets).length < 1) {
			dispatch(updateFilter('facets', { ...facets, activeHierarchicalFacets: {} }));
			dispatch(updateQueryString('facets', null));
		} else {
			dispatch(updateFilter('facets', { ...facets, activeHierarchicalFacets: newActiveHierarchicalFacets }));
		}
	};

	/**
	 *
	 * @param property
	 * @param facetsToUpdate
	 */
	const updateFacets = (property: string, facetsToUpdate: string[]) => {
		// convert the array into an object
		let facetsToAdd = {};
		facetsToUpdate.forEach((facet) => {
			// TODO: Should these default to ON?  This is only ever called with an empty array so far.
			facetsToAdd[facet] = FACET_STATE.ON;
		});

		let newActiveFacets = {
			...activeFacets,
			[property]: facetsToAdd,
		};
		// remove property if the facetsToUpdate was empty
		if (Object.keys(facetsToUpdate).length < 1) {
			delete newActiveFacets[property];
		}
		// reset the facets if there are no more properties left inside
		if (Object.keys(newActiveFacets).length < 1) {
			dispatch(updateFilter('facets', { ...facets, activeFacets: {} }));
			dispatch(updateQueryString('facets', null));
		} else {
			dispatch(updateFilter('facets', { ...facets, activeFacets: newActiveFacets }));
		}
	};

	/**
	 * update an entire property's set of hierarchicalFacets
	 * passing an empty array of hierarchicalFacets will clear out a single property's hierarchicalFacets
	 * @param property
	 * @param facetsToUpdate
	 */
	const updateHierarchicalFacets = (property: string, facetsToUpdate: string[]) => {
		// convert the array into an object
		let facetsToAdd = {};
		facetsToUpdate.forEach((facet) => {
			// TODO: Should these default to ON?  This is only ever called with an empty array so far.
			facetsToAdd[facet] = FACET_STATE.ON;
		});

		let newActiveHierarchicalFacets = {
			...activeHierarchicalFacets,
			[property]: facetsToAdd,
		};
		// remove property if the facetsToUpdate was empty
		if (Object.keys(facetsToUpdate).length < 1) {
			delete newActiveHierarchicalFacets[property];
		}
		// reset the hierarchicalFacets if there are no more properties left inside
		if (Object.keys(newActiveHierarchicalFacets).length < 1) {
			dispatch(updateFilter('facets', { ...facets, activeHierarchicalFacets: {} }));
			dispatch(updateQueryString('facets', null));
		} else {
			dispatch(updateFilter('facets', { ...facets, activeHierarchicalFacets: newActiveHierarchicalFacets }));
		}
	};

	/**
	 * Tri-state response to whether the facet is currently selected or not
	 * "off", "on" or "unselected"
	 * @param property
	 * @param facet
	 */
	const getFacetState = (property: string, facet: string) => {
		return activeFacets?.[property]?.[facet] || FACET_STATE.UNSELECTED;
	};

	/**
	 * Tri-state response to whether the facet is currently selected or not
	 * "off", "on" or "unselected"
	 * @param property
	 * @param facet
	 */
	const getHierarchicalFacetState = (property: string, facet: string) => {
		return activeHierarchicalFacets?.[property]?.[facet] || FACET_STATE.UNSELECTED;
	};

	/** removes all properties and items. clean start */
	const resetAllFacets = useCallback(() => {
		dispatch(updateFilter('facets', { textFilter: '', activeFacets: {}, activeHierarchicalFacets: {} }));
		dispatch(updateQueryString('facets', null));
	}, [dispatch]);

	/**
	 *
	 * @param property
	 * @param parent
	 */
	const hasSelectedChild = (property: string, parent: string) => {
		const state = getActiveHierarchicalFacets();
		if (state.hasOwnProperty(property)) {
			return Object.keys(state[property]).some((child) => {
				if (child.includes(parent) && child !== parent) {
					if (state[property][child] === FACET_STATE.ON) {
						return true;
					}
				}
				return false;
			});
		}
		return false;
	};

	/**
	 *
	 * @param property
	 * @param parent
	 */
	const hasExcludedChild = (property: string, parent: string) => {
		const state = getActiveHierarchicalFacets();
		if (state.hasOwnProperty(property)) {
			return Object.keys(state[property]).some((child) => {
				if (child.includes(parent) && child !== parent) {
					if (state[property][child] === FACET_STATE.OFF) {
						return true;
					}
				}
				return false;
			});
		}
		return false;
	};

	return {
		getActiveFacets,
		getActiveHierarchicalFacets,
		getTextFilter,
		updateTextFilter,
		loadFacets,
		toggleFacet,
		toggleHierarchicalFacet,
		toggleExcludeFacet,
		toggleExcludeHierarchicalFacet,
		getFacetState,
		getHierarchicalFacetState,
		updateFacets,
		updateHierarchicalFacets,
		resetAllFacets,
		hasSelectedChild,
		hasExcludedChild,
	};
};
